import { useTranslation } from 'react-i18next';
import { Users, Calendar, Repeat } from 'lucide-react';
import { format } from 'date-fns';
import { Card } from '@/components/ui/card';
import { endTimeFromStart } from '@/utils/time';
import type { ServiceOption, ProviderOption } from '../../CreateAvailabilityDialog';
import type { RecurrenceType } from '../../RecurrenceSelector';
import type { EndType } from '../../EndDateSelector';
import type { GroupRecurrenceMode } from './GroupRecurrenceStep';

interface GroupReviewStepProps {
  service: ServiceOption | null;
  provider: ProviderOption | null;
  mode: GroupRecurrenceMode;

  // Recurring
  selectedDays: number[];
  recurrenceType: RecurrenceType;
  endType: EndType;
  endDate: Date | null;
  occurrences: number | null;

  // One-time
  oneTimeDate: Date | null;

  // Shared
  startTime: string;
}

const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;

export function GroupReviewStep({
  service,
  provider,
  mode,
  selectedDays,
  recurrenceType,
  endType,
  endDate,
  occurrences,
  oneTimeDate,
  startTime,
}: GroupReviewStepProps) {
  const { t } = useTranslation('staff');
  const duration = service?.duration_minutes ?? 0;
  const computedEnd = endTimeFromStart(startTime, duration);
  const capacity = service?.max_capacity ?? 0;

  const dayList = selectedDays
    .map((d) => t(`availability.days.${DAY_KEYS[d]}`, DAY_KEYS[d]))
    .join(', ');

  return (
    <div className="p-6 space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">
        {t('availability.groupClass.review.title', 'Review group class')}
      </h3>

      <Card className="p-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
            <Users className="w-5 h-5 text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900">{service?.name ?? '—'}</p>
            <p className="text-sm text-gray-500">
              {t('availability.groupClass.review.spotsPerClass', '{{count}} spots per class', {
                count: capacity,
              })}
            </p>
          </div>
        </div>

        {provider && (
          <div className="text-sm text-gray-700">
            <span className="text-gray-500">{t('availability.dialog.staffMember', 'Staff member')}:</span>{' '}
            <span className="font-medium">{provider.name}</span>
          </div>
        )}

        <div className="flex items-center gap-2 text-sm">
          <Calendar className="w-4 h-4 text-gray-400" />
          {mode === 'one_time' ? (
            <span>
              {oneTimeDate ? format(oneTimeDate, 'EEEE, PPP') : '—'}
            </span>
          ) : (
            <span>
              {dayList || t('availability.groupClass.review.noDays', 'No days selected')}
              {' · '}
              {t(`availability.recurrence.${recurrenceType}`, recurrenceType)}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 text-sm tabular-nums">
          <Repeat className="w-4 h-4 text-gray-400" />
          <span className="font-medium">{startTime}</span>
          <span className="text-gray-400">→</span>
          <span className="font-medium">{computedEnd}</span>
          <span className="text-gray-500">({duration} min)</span>
        </div>

        {mode === 'recurring' && endType !== 'never' && (
          <div className="text-sm text-gray-700">
            {endType === 'on_date' && endDate && (
              <>
                {t('availability.recurrence.endsOnDate', 'Ends on')}{' '}
                <span className="font-medium">{format(endDate, 'PPP')}</span>
              </>
            )}
            {endType === 'after_occurrences' && occurrences != null && (
              <>
                {t('availability.recurrence.endsAfterOccurrences', 'Ends after {{count}} occurrences', {
                  count: occurrences,
                })}
              </>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
